import type { Action } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import type { Observable } from 'rxjs'
import {
  EMPTY,
  catchError,
  concat,
  concatMap,
  defer,
  exhaustMap,
  filter,
  firstValueFrom,
  from,
  groupBy,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  timer,
  withLatestFrom
} from 'rxjs'
import type { Epic, TRepositories } from '../../index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState, SheetRef } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { IUser } from '../../../Model/index.ts'
import { analyseDrop, awaitingMates, sortDrop } from '../../../Services/OrderWizard/dropPipeline.ts'
import { DROP_NOTICE_MS, noticeOfDrop } from '../../../Services/OrderWizard/dropNotice.ts'
import { detect } from '../../../Services/OrderWizard/detectionClient.ts'
import { detectSequencingPlatforms } from '../../../Util/sequencingPlatformDetection.ts'
import { loadPrimerHistory, loadPrimerRegistry } from '../../../Services/OrderWizard/primerRegistryLoader.ts'
import { readSheet } from '../../../Services/OrderWizard/sheet/readSheet.ts'
import { newSheetState, restoreSheetChoices } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { hasBlockingQuestion } from '../../../Services/OrderWizard/questions.ts'
import { identifySheetPrimers } from '../../../Services/OrderWizard/targetPlan.ts'
import type { DroppedFile } from '../../../Services/OrderWizard/dropPipeline.ts'
import { toCreateOrderRequest } from '../../../Services/OrderWizard/orderSettingsPlan.ts'
import { attachFileHandles, fileHandle, retainFileHandles } from '../../../Services/OrderWizard/fileHandles.ts'
import { wizardUploads } from '../../../Services/OrderWizard/uploadManager.ts'
import type { UploadEvent } from '../../../Services/OrderWizard/uploadManager.ts'
import { describeOrderWriteRefusal } from '../Order/orderWriteRefusal.ts'
import { messageOf } from './messages.ts'
import { retryTransient } from './retryTransient.ts'
import { guarded } from './guarded.ts'
import { orderWritesEpic } from './OrderWizardWrites.ts'
import { reviewCheckEpic } from './OrderWizardSubmit.ts'
import {
  registryPrefetchEpic,
  sheetPrimersEpic,
  targetCheckEpic,
  submitChecksEpic,
  plateRunsEpic,
  estimatePrimersEpic,
  primerRediscoveryEpic,
  validateEstimatesEpic,
  diagnosePrimersEpic,
  archiveCensusEpic
} from './OrderWizardAnalyses.ts'
import { analysisTelemetryEpic, reportUploadEvent } from './OrderWizardTelemetry.ts'
import { restoreDraftsEpic, reconcileDraftEpic, persistDraftsEpic, discardDraftEpic } from './OrderWizardPersistence.ts'
import { deleteDiscardedOrderEpic, ordersOfDrafts, resumableOrdersEpic, resumeOrderEpic } from './OrderWizardResume.ts'
import type { RootState, WizardEpic } from './draftState.ts'
import { draftOf } from './draftState.ts'

/** The last dropped sheet wins; its columns are mapped against every FASTQ name the draft knows. */
const readSheets = (draftId: string, sheets: readonly DroppedFile[], fastqNames: readonly string[], ref?: SheetRef): Observable<Action> => {
  const last = sheets[sheets.length - 1]
  if (!last) {
    return EMPTY
  }
  return from(Promise.all([readSheet(last.file), loadPrimerRegistry()])).pipe(
    map(([parsed, registryRows]) => {
      const sheet = restoreSheetChoices(newSheetState({ key: last.key, name: last.file.name }, parsed, { fileNames: fastqNames }), ref)
      return wizard.sheetLoaded({ draftId, sheet: { ...sheet, registeredPrimers: identifySheetPrimers(sheet, registryRows) } })
    }),
    catchError((error: unknown) =>
      of(wizard.sheetFailed({ draftId, key: last.key, error: `The sheet could not be read: ${messageOf(error)}` }))
    )
  )
}

/** Detection and planning run one drop at a time, so a second drop is planned against the first one's settings. */
export const processDropEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.filesDropped.match),
    concatMap(
      guarded(
        'reading dropped files',
        (action: ReturnType<typeof wizard.filesDropped>) =>
          defer(() => firstValueFrom(state$)).pipe(
            mergeMap((state) => {
              const { draftId, keys, pool } = action.payload
              const draft = draftOf(state, draftId)
              if (!draft) {
                return EMPTY
              }
              const files = keys.map((key) => fileHandle(draftId, key)).filter((file): file is File => !!file)
              const drop = sortDrop(draft, files)
              /* a copy of a known file is held under the known file's key */
              attachFileHandles(draftId, drop.handles)
              const org = state.authentication.user?.org ?? {}
              const next = (): Action[] => {
                if (draft.orderId) {
                  return [wizard.uploadsRequested({ draftId })]
                }
                /* files of a draft restored before its order was created: nothing new to detect, just create it */
                if (drop.toAnalyse.length === 0 && drop.reattached.length > 0 && draft.settings && !hasBlockingQuestion(draft)) {
                  return [wizard.orderCreationRequested({ draftId })]
                }
                return []
              }
              const immediate: Action[] = [wizard.filesAdded({ draftId, files: drop.added })]
              if (drop.reattached.length > 0) {
                immediate.push(wizard.filesReattached({ draftId, keys: drop.reattached }))
              }
              const knownFastq = Object.values(draft.files)
                .filter((file) => file.kind === 'fastq')
                .map((file) => file.name)
              const sheets$ = readSheets(
                draftId,
                drop.sheets,
                [...knownFastq, ...drop.toAnalyse.map(({ file }) => file.name)],
                draft.sheetRef
              )
              const emitted: Action[] = []
              const settled$ = state$.pipe(
                take(1),
                mergeMap((latest) => {
                  const notice = noticeOfDrop({ id: uuidv4(), draftId, drop, actions: emitted, after: draftOf(latest, draftId), pool })
                  return notice ? of(wizard.dropSettled({ notice })) : EMPTY
                })
              )
              const recorded = (work$: Observable<Action>) =>
                concat(work$.pipe(tap((emittedAction) => emitted.push(emittedAction))), settled$)
              if (drop.toAnalyse.length === 0) {
                return recorded(concat(of(...immediate), sheets$, of(...next())))
              }
              return recorded(
                concat(
                  of(...immediate),
                  sheets$,
                  of(wizard.detectionStarted({ draftId })),
                  from(
                    analyseDrop(draft, [...drop.toAnalyse, ...awaitingMates(draft, drop, (key) => fileHandle(draftId, key))], org, {
                      detectPlatforms: detectSequencingPlatforms,
                      detect,
                      loadRegistry: loadPrimerRegistry,
                      loadHistory: loadPrimerHistory
                    })
                  ).pipe(
                    withLatestFrom(state$),
                    mergeMap(([analysis, latest]) => {
                      const created: Action[] = [wizard.detectionCompleted({ draftId, ...analysis, ...(pool ? { pool } : {}) })]
                      /* the latest draft holds a sheet this drop brought, which may settle the target */
                      const current = draftOf(latest, draftId) ?? draft
                      const provisional = {
                        ...current,
                        discovery: analysis.discovery ?? current.discovery,
                        settings: analysis.settings ?? current.settings,
                        groups: { ...current.groups, ...Object.fromEntries(analysis.groups.map((group) => [group.key, group])) }
                      }
                      if (!draft.orderId && analysis.groups.length > 0 && !hasBlockingQuestion(provisional)) {
                        created.push(wizard.orderCreationRequested({ draftId }))
                      }
                      return of(...created, ...next())
                    }),
                    catchError((error: unknown) => {
                      console.error('Order wizard detection failed', error)
                      return of(wizard.detectionFailed({ draftId, error: messageOf(error), keys: drop.toAnalyse.map(({ key }) => key) }))
                    })
                  )
                )
              )
            })
          ),
        (action, error) => wizard.detectionFailed({ draftId: action.payload.draftId, error })
      )
    )
  )

const CREATE_TIMEOUT_MS = 60_000
/* allows for the server's clock running behind the browser's */
const CLOCK_SKEW_MS = 5 * 60_000

/**
 * The order an earlier attempt for this draft may have made although its answer never arrived: same
 * name, made by this user while one of the draft's attempts was in flight, and held by no other draft
 * of the user's (the default name is shared by every draft started that day).
 */
const earlierAttempt = async (
  draft: DraftState,
  user: IUser | undefined,
  taken: ReadonlySet<string>,
  repository: TRepositories['OrderRepository']
) => {
  const windows = (draft.creationAttempts ?? []).map((at) => [
    Date.parse(at) - CLOCK_SKEW_MS,
    Date.parse(at) + CREATE_TIMEOUT_MS + CLOCK_SKEW_MS
  ])
  if (!user || windows.length === 0) {
    return undefined
  }
  const found = await firstValueFrom(
    repository.getAllOrders({ size: 20, page: 0, filters: [{ value: draft.name, parameter: 'name', filterType: 'EQUAL' }] }, user)
  )
  const madeDuringAttempt = (created: string | undefined) => {
    const at = created ? Date.parse(created) : NaN
    return windows.some(([from, until]) => at >= from && at <= until)
  }
  return (found?.content ?? [])
    .map((entry) => entry.order)
    .find(
      (order) =>
        order.name === draft.name && order.createdByUser === user.id && !taken.has(order.id) && madeDuringAttempt(order.orderCreated)
    )?.id
}

const orderIdFor = (
  draft: DraftState,
  user: IUser | undefined,
  taken: () => ReadonlySet<string>,
  repository: TRepositories['OrderRepository']
): Observable<{ id: string; created: boolean }> => {
  if (draft.orderId) {
    return of({ id: draft.orderId, created: false })
  }
  const create$ = defer(() => repository.createOrder(toCreateOrderRequest(draft.name, draft.settings!, draft.description), user)).pipe(
    timeout({ first: CREATE_TIMEOUT_MS, with: () => throwError(() => new Error('the server did not answer in time')) }),
    map((order) => ({ id: order.id, created: true }))
  )
  if (!draft.creationAttempts?.length) {
    return create$
  }
  return defer(() => earlierAttempt(draft, user, taken(), repository)).pipe(mergeMap((id) => (id ? of({ id, created: true }) : create$)))
}

const poolIdFor = (
  draft: DraftState,
  orderId: string,
  existingOrder: boolean,
  user: IUser | undefined,
  repository: TRepositories['OrderRepository']
) => {
  if (draft.poolIds[0]) {
    return of(draft.poolIds[0])
  }
  const create$ = defer(() => repository.createSamplePool({ orderId }, user) as Observable<{ id: string }>).pipe(map((pool) => pool.id))
  if (!existingOrder) {
    return create$
  }
  return repository.getAllSamplePools({ orderId }).pipe(mergeMap((pools) => (pools?.[0]?.id ? of(pools[0].id) : create$)))
}

const trimmed = (text: string | undefined) => text?.trim() ?? ''

/* name and description stay editable on the start screen while the creation request is in flight */
const detailsEditedMeanwhile = (sent: DraftState, state$: Observable<RootState>): Observable<Action> =>
  state$.pipe(
    take(1),
    mergeMap((state) => {
      const latest = draftOf(state, sent.id)
      const changed = !!latest && (trimmed(latest.name) !== trimmed(sent.name) || trimmed(latest.description) !== trimmed(sent.description))
      return changed && latest.name.trim()
        ? of(wizard.orderDetailsSaveRequested({ draftId: latest.id, name: latest.name, description: latest.description }))
        : EMPTY
    })
  )

/**
 * Creates the order and its first pool, or only what is still missing. Exhaust per draft: a second
 * request while one is in flight must not create a second order. An attempt whose answer never came is
 * recorded, so trying again first looks for the order it may have made.
 */
export const createOrderEpic: WizardEpic = (action$, state$, { OrderRepository }) =>
  action$.pipe(
    filter(wizard.orderCreationRequested.match),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        withLatestFrom(state$),
        exhaustMap(
          guarded(
            'creating the order',
            ([action, state]: [ReturnType<typeof wizard.orderCreationRequested>, RootState]) => {
              const { draftId } = action.payload
              const draft = draftOf(state, draftId)
              if (!draft?.settings) {
                return of(wizard.orderCreationFailed({ draftId, error: 'Nothing was detected to create the order from' }))
              }
              const user = state.authentication.user
              return concat(
                draft.orderId ? EMPTY : of(wizard.orderCreationAttempted({ draftId, at: new Date().toISOString() })),
                orderIdFor(draft, user, () => ordersOfDrafts(state, user), OrderRepository).pipe(
                  mergeMap(({ id: orderId, created }) =>
                    concat(
                      created ? of(wizard.orderCreated({ draftId, orderId })) : EMPTY,
                      poolIdFor(draft, orderId, !created || !!draft.creationAttempts?.length, user, OrderRepository).pipe(
                        mergeMap((poolId) => of(wizard.poolCreated({ draftId, poolId }), wizard.uploadsRequested({ draftId })))
                      ),
                      created ? detailsEditedMeanwhile(draft, state$) : EMPTY
                    )
                  )
                )
              ).pipe(
                catchError((error: unknown) =>
                  of(wizard.orderCreationFailed({ draftId, error: `Creating the order failed: ${messageOf(error)}` }))
                )
              )
            },
            ([action], error) => wizard.orderCreationFailed({ draftId: action.payload.draftId, error })
          )
        )
      )
    )
  )

export const startUploadsEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.uploadsRequested.match),
    withLatestFrom(state$),
    mergeMap(
      guarded('starting uploads', ([action, state]) => {
        const draft = draftOf(state, action.payload.draftId)
        const poolId = draft?.poolIds[0]
        if (!draft?.orderId || !poolId) {
          return EMPTY
        }
        const ready = Object.values(draft.files)
          .filter((file) => file.upload?.status === 'pending')
          .flatMap((file) => {
            const handle = fileHandle(draft.id, file.key)
            return handle ? [{ key: file.key, file: handle }] : []
          })
        if (ready.length > 0) {
          wizardUploads.enqueue(draft.id, { orderId: draft.orderId, poolId }, ready)
        }
        return EMPTY
      })
    )
  )

const uploadEventAction = (event: UploadEvent): Action => {
  switch (event.type) {
    case 'started':
      return wizard.uploadStarted({ draftId: event.draftId, key: event.key })
    case 'progress':
      return wizard.uploadProgressed({ draftId: event.draftId, key: event.key, progress: event.progress })
    case 'succeeded':
      return wizard.uploadSucceeded({ draftId: event.draftId, key: event.key, serverFileId: event.serverFileId })
    case 'failed':
      return wizard.uploadFailed({ draftId: event.draftId, key: event.key, error: event.error })
    case 'retrying':
      return wizard.uploadRetrying({ draftId: event.draftId, key: event.key, message: event.message })
  }
}

export const uploadEventsEpic: Epic = () => wizardUploads.events$.pipe(tap(reportUploadEvent), map(uploadEventAction))

/** Removed items' files stop uploading: whatever the draft no longer holds is cancelled and let go. */
export const removedFilesEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.itemsRemoved.match),
    withLatestFrom(state$),
    mergeMap(
      guarded('cancelling removed uploads', ([action, state]) => {
        const draft = draftOf(state, action.payload.draftId)
        if (draft) {
          const kept = new Set(Object.keys(draft.files))
          wizardUploads.retain(draft.id, kept)
          retainFileHandles(draft.id, kept)
        }
        return EMPTY
      })
    )
  )

export const retryUploadEpic: Epic = (action$) =>
  action$.pipe(
    filter(wizard.uploadRetryRequested.match),
    mergeMap(
      guarded('retrying an upload', (action) => {
        const { draftId, key } = action.payload
        return wizardUploads.retry(draftId, key) ? EMPTY : of(wizard.uploadNeedsReadd({ draftId, key }))
      })
    )
  )

/**
 * Title and description go through the same order update as the classic view. Every other field is sent
 * as the order holds it right now, read immediately before, so the edit cannot overwrite a change made in
 * the classic view meanwhile.
 */
export const saveOrderDetailsEpic: Epic = (action$, state$, { OrderRepository }) =>
  action$.pipe(
    filter(wizard.orderDetailsSaveRequested.match),
    groupBy((action) => action.payload.draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        concatMap(
          guarded(
            'saving the order details',
            (action: ReturnType<typeof wizard.orderDetailsSaveRequested>) => saveOrderDetails(action, state$, OrderRepository),
            (action, error) => wizard.orderDetailsSaveFailed({ draftId: action.payload.draftId, error })
          )
        )
      )
    )
  )

const saveOrderDetails = (
  action: ReturnType<typeof wizard.orderDetailsSaveRequested>,
  state$: Observable<RootState>,
  OrderRepository: TRepositories['OrderRepository']
): Observable<Action> =>
  defer(() => firstValueFrom(state$)).pipe(
    mergeMap((state) => {
      const { draftId, name, description } = action.payload
      const orderId = draftOf(state, draftId)?.orderId
      if (!orderId || !name.trim()) {
        return EMPTY
      }
      /* the write queue may update the order meanwhile (its settings); a conflict reads the new version and saves again */
      return defer(() => OrderRepository.getOrder(orderId)).pipe(
        mergeMap((order) =>
          OrderRepository.updateOrderName({
            orderId,
            version: order.version ?? '0',
            name: name.trim(),
            description: description?.trim() ?? '',
            taxa: order.targetTaxonomy,
            targetRegion: order.targetRegion,
            allowSingletons: order.allowSingletons,
            processingType: order.processingType,
            redlistMode: order.redlistMode ?? 'NONE',
            sampleConfiguration: order.sampleConfiguration
          })
        ),
        retryTransient({ conflicts: true, delaysMs: CONFLICT_DELAYS_MS }),
        map((saved) => wizard.orderDetailsSaved({ draftId, name: saved.name, description: saved.description ?? undefined })),
        catchError((error: unknown) =>
          of(
            wizard.orderDetailsSaveFailed({
              draftId,
              error: describeOrderWriteRefusal(error) ?? `Saving the order failed: ${messageOf(error)}`
            })
          )
        )
      )
    })
  )

const CONFLICT_DELAYS_MS = [300, 1000, 3000]

const RELEASES = [wizard.questionAnswered, wizard.sheetLoaded, wizard.sheetPrimersIdentified, wizard.sheetMappingChanged]

/** Whether the draft waits for an order creation that nothing holds back any more. */
const creationDue = (draft: DraftState | undefined): draft is DraftState =>
  !!draft &&
  !draft.orderId &&
  draft.phase === 'start' &&
  !!draft.settings &&
  Object.keys(draft.groups).length > 0 &&
  !hasBlockingQuestion(draft)

/**
 * An answer, or a sheet whose primers settle the target, may release the order creation a blocking
 * question held back.
 */
export const releaseOrderCreationEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter((action) => RELEASES.some((release) => release.match(action))),
    withLatestFrom(state$),
    mergeMap(
      guarded('releasing the order creation', ([action, state]) => {
        const draft = draftOf(state, (action as ReturnType<(typeof RELEASES)[number]>).payload.draftId)
        return creationDue(draft) ? of(wizard.orderCreationRequested({ draftId: draft.id })) : EMPTY
      })
    )
  )

/**
 * A draft restored before its order was created starts the creation a drop would have. An attempt the
 * page that went away may still have in flight is waited out first, so the creation finds the order it made.
 */
export const restoredOrderCreationEpic: Epic = (action$, state$) =>
  action$.pipe(
    filter(wizard.draftsRestored.match),
    withLatestFrom(state$),
    mergeMap(
      guarded('resuming the order creation', ([action, state]) =>
        from(action.payload.drafts.filter((restored) => creationDue(draftOf(state, restored.id)))).pipe(
          mergeMap((restored) => {
            const lastAttempt = restored.creationAttempts?.at(-1)
            const wait = lastAttempt ? Date.parse(lastAttempt) + CREATE_TIMEOUT_MS - Date.now() : 0
            return (wait > 0 ? timer(wait) : of(0)).pipe(
              withLatestFrom(state$),
              mergeMap(([, latest]) =>
                creationDue(draftOf(latest, restored.id)) ? of(wizard.orderCreationRequested({ draftId: restored.id })) : EMPTY
              )
            )
          })
        )
      )
    )
  )

/** A drop's confirmation goes by itself after a while; a newer drop's replaces it and starts the wait again. */
export const dropNoticeEpic: Epic = (action$) =>
  action$.pipe(
    filter(wizard.dropSettled.match),
    switchMap(({ payload: { notice } }) => timer(DROP_NOTICE_MS).pipe(map(() => wizard.dropNoticeDismissed({ id: notice.id }))))
  )

export const orderWizardEpicList: WizardEpic[] = [
  orderWritesEpic,
  processDropEpic,
  dropNoticeEpic,
  createOrderEpic,
  startUploadsEpic,
  uploadEventsEpic,
  retryUploadEpic,
  removedFilesEpic,
  saveOrderDetailsEpic,
  releaseOrderCreationEpic,
  restoredOrderCreationEpic,
  registryPrefetchEpic,
  sheetPrimersEpic,
  targetCheckEpic,
  estimatePrimersEpic,
  primerRediscoveryEpic,
  plateRunsEpic,
  submitChecksEpic,
  reviewCheckEpic,
  validateEstimatesEpic,
  diagnosePrimersEpic,
  archiveCensusEpic,
  restoreDraftsEpic,
  reconcileDraftEpic,
  persistDraftsEpic,
  discardDraftEpic,
  resumableOrdersEpic,
  resumeOrderEpic,
  deleteDiscardedOrderEpic,
  analysisTelemetryEpic
]
